import type { MediaRow } from '@/routes/media/types';
import type { PublishStatus } from '@/components/editor/PublishingSidebar';
import type { TranslatedValue } from '@/lib/forms/translatable';
import type { TaxonomyRef } from '@/routes/news/types';

/**
 * The five simple content modules — docs/09-BUILD-PLAN.md Phase 6 step 5, plus
 * the projects and vacancies its numbered list never names.
 *
 * Hand-written for the reason `lib/api/resource.ts` sets out: Scramble types a
 * translatable field as `{[key: string]: unknown}` because it cannot see
 * through the per-locale map `AdminResource::translations()` builds.
 */

/** Everything §6's publishing sidebar reads, on every one of these. */
interface Publishable {
  status: PublishStatus;
  published_at: string | null;
  pending_review: boolean;
  translations_available: string[];
  translation_completeness: Record<string, number>;
  created_at: string | null;
  updated_at: string | null;
  deleted_at: string | null;
  updated_by: { id: number; name: string } | null;
}

// ── announcements ─────────────────────────────────────────────────────

/** docs/03-DATA-MODEL.md §4's `category` enum — it picks the card's icon. */
export const ANNOUNCEMENT_CATEGORIES = [
  'admission',
  'mobility',
  'exams',
  'competition',
  'general',
] as const;

export type AnnouncementCategory = (typeof ANNOUNCEMENT_CATEGORIES)[number];

export interface AnnouncementRow extends Publishable {
  id: number;
  title: TranslatedValue;
  body: TranslatedValue;
  display_date: string;
  category: AnnouncementCategory;
  icon: string | null;
  document_id: number | null;
  document: { id: number; title: TranslatedValue } | null;
  external_url: string | null;
  is_pinned: boolean;
  is_featured: boolean;
  expires_at: string | null;
  sort_order: number;
}

export interface AnnouncementInput {
  title: TranslatedValue;
  body: TranslatedValue;
  display_date: string;
  category: AnnouncementCategory;
  icon: string | null;
  document_id: number | null;
  external_url: string | null;
  is_pinned: boolean;
  /** §1.3's hero countdown card. Independent of `is_pinned` — see the editor. */
  is_featured: boolean;
  expires_at: string | null;
  sort_order: number;
  status: string;
  published_at: string | null;
  pending_review: boolean;
}

// ── events ────────────────────────────────────────────────────────────

export interface EventRow extends Publishable {
  id: number;
  title: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  location: TranslatedValue;
  starts_at: string;
  ends_at: string | null;
  cover_media_id: number | null;
  cover: MediaRow | null;
  /*
   * `cover_url`, `photo_url` and `logo_url` were removed here.
   *
   * Task #71 moved images onto the media library and the API resources
   * went from a resolved URL string to the media object above. These
   * types kept declaring the old fields, so three list cells went on
   * reading `undefined` and rendering their empty state — the news list
   * showed a placeholder against 676 articles that had a cover, and
   * nothing failed. Use `thumbUrl(row.cover)`.
   */
  category: string | null;
  registration_url: string | null;
  is_featured: boolean;
  video_url: string | null;
  video_poster_media_id: number | null;
  video_poster: MediaRow | null;
}

export interface EventInput {
  title: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  location: TranslatedValue;
  starts_at: string;
  ends_at: string | null;
  cover_media_id: number | null;
  category: string | null;
  registration_url: string | null;
  is_featured: boolean;
  video_url: string | null;
  video_poster_media_id: number | null;
  status: string;
  published_at: string | null;
  pending_review: boolean;
}

// ── clubs ─────────────────────────────────────────────────────────────

/** The platforms docs/01-INFORMATION-ARCHITECTURE.md §12 draws an icon for. */
export const CLUB_PLATFORMS = [
  'facebook',
  'instagram',
  'youtube',
  'linkedin',
  'x',
  'website',
] as const;

export type ClubPlatform = (typeof CLUB_PLATFORMS)[number];

export interface ClubRow extends Publishable {
  id: number;
  name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  body: TranslatedValue;
  cover_media_id: number | null;
  cover: MediaRow | null;
  contact_email: string | null;
  contact_person: TranslatedValue;
  /** Always an object, never null — see `ClubResource`. */
  social_links: Partial<Record<ClubPlatform, string>>;
  sort_order: number;
}

export interface ClubInput {
  name: TranslatedValue;
  slug: TranslatedValue;
  description: TranslatedValue;
  body: TranslatedValue;
  cover_media_id: number | null;
  contact_email: string | null;
  contact_person: TranslatedValue;
  social_links: Partial<Record<ClubPlatform, string>>;
  sort_order: number;
  status: string;
  published_at: string | null;
  pending_review: boolean;
}

// ── projects ──────────────────────────────────────────────────────────

export interface ProjectRow extends Publishable {
  id: number;
  /** The acronym — PRODIGE, ERIC-GE. Not translatable, on purpose. */
  name: string;
  title: TranslatedValue;
  slug: TranslatedValue;
  summary: TranslatedValue;
  body: TranslatedValue;
  funder: TranslatedValue;
  partners: Record<string, string[] | null>;
  programme: string | null;
  starts_on: string | null;
  ends_on: string | null;
  budget: string | number | null;
  coordinator_staff_id: number | null;
  coordinator: { id: number; name: string } | null;
  logo_media_id: number | null;
  logo: MediaRow | null;
  cover_media_id: number | null;
  cover: MediaRow | null;
  website_url: string | null;
  sort_order: number;
}

export interface ProjectInput {
  name: string;
  title: TranslatedValue;
  slug: TranslatedValue;
  summary: TranslatedValue;
  body: TranslatedValue;
  funder: TranslatedValue;
  partners: Record<string, string[] | null>;
  programme: string | null;
  starts_on: string | null;
  ends_on: string | null;
  budget: number | null;
  coordinator_staff_id: number | null;
  logo_media_id: number | null;
  cover_media_id: number | null;
  website_url: string | null;
  sort_order: number;
  status: string;
  published_at: string | null;
  pending_review: boolean;
}

// ── vacancies ─────────────────────────────────────────────────────────

/** docs/03-DATA-MODEL.md §9's `type` enum. */
export const VACANCY_TYPES = ['academic', 'administrative', 'vocational'] as const;

export type VacancyType = (typeof VACANCY_TYPES)[number];

export interface VacancyRow extends Publishable {
  id: number;
  title: TranslatedValue;
  slug: TranslatedValue;
  unit: TranslatedValue;
  description: TranslatedValue;
  requirements: TranslatedValue;
  required_documents: Record<string, string[] | null>;
  application_instructions: TranslatedValue;
  type: VacancyType;
  faculty_id: number | null;
  faculty: TaxonomyRef | null;
  deadline: string;
  result_document_id: number | null;
  is_result_published: boolean;
  /** Computed by the server, in the university's timezone rather than yours. */
  is_closed: boolean;
}

export interface VacancyInput {
  title: TranslatedValue;
  slug: TranslatedValue;
  unit: TranslatedValue;
  description: TranslatedValue;
  requirements: TranslatedValue;
  required_documents: Record<string, string[] | null>;
  application_instructions: TranslatedValue;
  type: VacancyType;
  faculty_id: number | null;
  deadline: string;
  result_document_id: number | null;
  is_result_published: boolean;
  status: string;
  published_at: string | null;
  pending_review: boolean;
}

/**
 * A `datetime-local` value from an ISO string, in *local* time.
 *
 * `toISOString()` would return UTC and show an editor in Tbilisi a time four
 * hours off the one they chose. Shared with `PublishingSidebar`, which learned
 * the same lesson first.
 */
export function toLocalDateTime(iso: string | null): string {
  if (!iso) {
    return '';
  }

  const date = new Date(iso);

  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const pad = (value: number) => String(value).padStart(2, '0');

  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  );
}

export function fromLocalDateTime(value: string): string | null {
  if (value === '') {
    return null;
  }

  const date = new Date(value);

  return Number.isNaN(date.getTime()) ? null : date.toISOString();
}
